<template>
  <div class="timeline" v-if="history.length > 0">
    <div
      v-for="(entry, i) in history"
      :key="entry.id || i"
      class="timeline-item"
    >
      <div class="timeline-dot" :class="getStatusClass(entry.to_status)"></div>
      <div class="timeline-line" v-if="i < history.length - 1"></div>
      <div class="timeline-content">
        <div class="timeline-status">
          <CaseStatusBadge :status="entry.to_status" />
          <span v-if="entry.from_status" class="timeline-from">
            from {{ getStatusLabel(entry.from_status) }}
          </span>
        </div>
        <div v-if="entry.reason" class="timeline-reason">{{ entry.reason }}</div>
        <div class="timeline-meta">
          <span class="timeline-by">{{ entry.changed_by_type }}</span>
          <span class="timeline-date">{{ formatDate(entry.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
  <p v-else class="empty-text">{{ $t('case.noHistory') }}</p>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import CaseStatusBadge from './CaseStatusBadge.vue'
import { statusVariants, statusLabelKeys } from '../config/statusConfig.js'
import dayjs from 'dayjs'

const { t } = useI18n()

defineProps({
  history: { type: Array, default: () => [] },
})

const getStatusClass = (status) => statusVariants[status] || 'default'

const getStatusLabel = (status) => {
  const key = statusLabelKeys[status]
  return key ? t(key) : status.replace(/_/g, ' ')
}

const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY, HH:mm')
}
</script>

<style scoped>
.timeline {
  position: relative;
  padding-left: var(--space-6);
}

.timeline-item {
  position: relative;
  padding-bottom: var(--space-6);
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: calc(-1 * var(--space-6) - 5px);
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-gray-300);
  border: 2px solid var(--color-white);
}

.timeline-dot.success { background: #10b981; }
.timeline-dot.warning { background: #f59e0b; }
.timeline-dot.error { background: #ef4444; }
.timeline-dot.info { background: #3b82f6; }

.timeline-line {
  position: absolute;
  left: calc(-1 * var(--space-6));
  top: 18px;
  bottom: 0;
  width: 1px;
  background: var(--color-gray-200);
}

.timeline-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.timeline-status {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.timeline-from {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

.timeline-reason {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  background: var(--color-gray-50);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
}

.timeline-meta {
  display: flex;
  gap: var(--space-3);
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

.timeline-by {
  text-transform: capitalize;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-6);
}
</style>
